/**
 * Plain-text fields whose input value is exactly what the form should hold.
 *
 * Browser autofill and password managers write straight into the DOM, and the
 * change event does not always reach React - the box then looks filled while
 * the form still considers it empty, and Save refuses with no obvious reason.
 * Reconciling these at submit time makes a prefilled form behave like a typed
 * one.
 *
 * Deliberately excludes anything formatted or non-text: currency inputs render
 * "$100.00", dates "09/15/2026", and dropdowns hold ids rather than labels, so
 * copying their display text back into the form would corrupt the value.
 */
const RECONCILABLE_TEXT_FIELDS = [
  "name",
  "surname",
  "companyName",
  "contactName",
  "title",
  "email",
  "phone",
  "mobile",
  "fax",
  "website",
  "ZIP",
  "address",
  "street",
  "billingContact",
  "billingAddress",
  "billingZIP",
  "billingStreet",
] as const;

export const collectAutofilledValues = (
  form: HTMLFormElement | null | undefined,
  getValue: (field: string) => unknown
): Array<[string, string]> => {
  if (!form) {
    return [];
  }

  return RECONCILABLE_TEXT_FIELDS.reduce<Array<[string, string]>>((changes, field) => {
    const input = form.querySelector<HTMLInputElement>(`input[name="${field}"]`);
    const domValue = input?.value?.trim();

    // Only ever fill a gap - never clear or overwrite what the form already has.
    if (domValue && domValue !== String(getValue(field) ?? "").trim()) {
      changes.push([field, domValue]);
    }

    return changes;
  }, []);
};
